import { sanitizeProductHtml } from './product-html';

const SHOPIFY_DOMAIN = (process.env.GATSBY_SHOPIFY_STORE_DOMAIN || '')
  .replace(/^https?:\/\//, '')
  .replace(/\/$/, '');
const SHOPIFY_TOKEN = process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || '';
const STOREFRONT_API_VERSION = '2024-10';

export const isStorefrontConfigured = Boolean(SHOPIFY_DOMAIN && SHOPIFY_TOKEN);

export const storefrontFetch = async <T = any>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> => {
  if (!isStorefrontConfigured) {
    throw new Error('Storefront API not configured');
  }
  const res = await fetch(
    `https://${SHOPIFY_DOMAIN}/api/${STOREFRONT_API_VERSION}/graphql.json`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }
  );
  if (!res.ok) throw new Error(`Storefront API ${res.status}: ${await res.text()}`);
  const json = await res.json();
  if (json.errors) throw new Error(`Storefront API errors: ${JSON.stringify(json.errors)}`);
  return json.data as T;
};

export interface LiveProduct {
  shopifyId: string;
  handle: string;
  title: string;
  productType: string;
  priceRangeV2: {
    minVariantPrice: { amount: string };
    maxVariantPrice: { amount: string };
  };
  featuredImage: { altText: string | null; originalSrc: string } | null;
  availableForSale: boolean;
}

export const fetchLiveProducts = async (): Promise<LiveProduct[]> => {
  const data = await storefrontFetch<{
    collection: { products: { edges: { node: any }[] } } | null;
  }>(`
    query FrontpageProducts {
      collection(handle: "frontpage") {
        products(first: 100) {
          edges {
            node {
              id
              handle
              title
              productType
              availableForSale
              priceRange {
                minVariantPrice { amount }
                maxVariantPrice { amount }
              }
              featuredImage { altText url }
            }
          }
        }
      }
    }
  `);
  if (!data.collection) return [];
  return data.collection.products.edges.map((e) => ({
    shopifyId: e.node.id,
    handle: e.node.handle,
    title: e.node.title,
    productType: e.node.productType,
    availableForSale: Boolean(e.node.availableForSale),
    priceRangeV2: {
      minVariantPrice: { amount: e.node.priceRange.minVariantPrice.amount },
      maxVariantPrice: { amount: e.node.priceRange.maxVariantPrice.amount },
    },
    featuredImage: e.node.featuredImage
      ? {
          altText: e.node.featuredImage.altText,
          originalSrc: e.node.featuredImage.url,
        }
      : null,
  }));
};

export interface LiveVariant {
  shopifyId: string;
  title: string;
  sku: string | null;
  price: string;
  availableForSale: boolean;
  selectedOptions: { name: string; value: string }[];
  image: { originalSrc: string; altText: string | null } | null;
}

export interface LiveProductDetail {
  shopifyId: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  productType: string;
  images: { id: string; altText: string | null; originalSrc: string }[];
  variants: LiveVariant[];
}

export const validateVariantIds = async (
  ids: string[]
): Promise<{ valid: Set<string>; available: Set<string> }> => {
  if (ids.length === 0) return { valid: new Set(), available: new Set() };
  const data = await storefrontFetch<{ nodes: Array<any | null> }>(
    `query Validate($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
        }
      }
    }`,
    { ids }
  );
  const valid = new Set<string>();
  const available = new Set<string>();
  for (const node of data.nodes) {
    if (node && node.id) {
      valid.add(node.id);
      if (node.availableForSale) available.add(node.id);
    }
  }
  return { valid, available };
};

export const fetchLiveProductByHandle = async (
  handle: string
): Promise<LiveProductDetail | null> => {
  const data = await storefrontFetch<{ product: any }>(`
    query ProductByHandle($handle: String!) {
      product(handle: $handle) {
        id
        handle
        title
        description
        descriptionHtml
        productType
        images(first: 20) {
          edges { node { id altText url } }
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              sku
              availableForSale
              price { amount }
              selectedOptions { name value }
              image { url altText }
            }
          }
        }
      }
    }
  `, { handle });

  if (!data.product) return null;

  return {
    shopifyId: data.product.id,
    handle: data.product.handle,
    title: data.product.title,
    description: data.product.description,
    descriptionHtml: sanitizeProductHtml(data.product.descriptionHtml),
    productType: data.product.productType,
    images: data.product.images.edges.map((e: any) => ({
      id: e.node.id,
      altText: e.node.altText,
      originalSrc: e.node.url,
    })),
    variants: data.product.variants.edges.map((e: any) => ({
      shopifyId: e.node.id,
      title: e.node.title,
      sku: e.node.sku,
      price: e.node.price?.amount ?? '0',
      availableForSale: e.node.availableForSale,
      selectedOptions: e.node.selectedOptions ?? [],
      image: e.node.image
        ? { originalSrc: e.node.image.url, altText: e.node.image.altText }
        : null,
    })),
  };
};
